import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Minus, ArrowRight, Home, Building2, Boxes, MapPinned, Camera } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TIER_LABEL = { null: "Sans formule", ESSENTIEL: "Avec Essentiel", COMPLETE: "Avec Pack complet" };

const ESSENTIEL_ITEMS = [
  "Étude et définition du projet",
  "Conception",
  "Plans nécessaires à la mission",
  "Démarches administratives selon le projet",
  "Consultation des entreprises",
  "Analyse et comparaison des devis",
  "Préparation du projet avant travaux",
];

const COMPLETE_ITEMS = [
  "Tout ce qui est compris dans Essentiel",
  "Organisation du chantier",
  "Coordination des entreprises",
  "Suivi du chantier",
  "Suivi de l'avancement",
  "Suivi des situations / factures",
  "Photos et suivi chantier",
  "Accompagnement jusqu'à la réception",
];

const COMPARISON_ROWS = [
  { label: "Conception", essentiel: true, complete: true },
  { label: "Plans", essentiel: true, complete: true },
  { label: "Démarches administratives", essentiel: true, complete: true },
  { label: "Consultation entreprises", essentiel: true, complete: true },
  { label: "Comparaison des devis", essentiel: true, complete: true },
  { label: "Préparation chantier", essentiel: true, complete: true },
  { label: "Suivi du chantier", essentiel: false, complete: true },
  { label: "Coordination entreprises", essentiel: false, complete: true },
  { label: "Suivi situations / factures", essentiel: false, complete: true },
  { label: "Suivi photo chantier", essentiel: false, complete: true },
  { label: "Réception du chantier", essentiel: false, complete: true },
];

const FormulaCard = ({ testid, title, tagline, items, renoRate, neuveRate, highlight, onCta }) => (
  <div
    data-testid={testid}
    className={`relative flex h-full flex-col rounded-3xl border p-8 sm:p-10 ${
      highlight ? "border-cognac bg-navy text-white shadow-2xl" : "border-stone-200 bg-white"
    }`}
  >
    {highlight && (
      <span className="absolute -top-3 left-8 rounded-full bg-cognac px-4 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white">
        Jusqu'à la réception
      </span>
    )}
    <p className={`text-xs font-bold uppercase tracking-[0.25em] ${highlight ? "text-cognac-light" : "text-cognac"}`}>Formule</p>
    <h3 className={`mt-3 font-display text-2xl font-medium tracking-tight sm:text-3xl ${highlight ? "text-white" : "text-navy"}`}>{title}</h3>
    <p className={`mt-2 text-sm ${highlight ? "text-white/70" : "text-stone-500"}`}>{tagline}</p>

    <ul className="mt-6 space-y-2.5">
      {items.map((it) => (
        <li key={it} className={`flex items-start gap-2.5 text-sm leading-snug ${highlight ? "text-white/85" : "text-stone-600"}`}>
          <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${highlight ? "text-cognac-light" : "text-sage"}`} />
          {it}
        </li>
      ))}
    </ul>

    <div className={`mt-8 space-y-3 rounded-2xl p-5 ${highlight ? "bg-white/10" : "bg-cream"}`}>
      <div className="flex items-center justify-between gap-4">
        <span className={`flex items-center gap-2 text-sm font-semibold ${highlight ? "text-white" : "text-navy"}`}>
          <Home className="h-4 w-4" /> Rénovation
        </span>
        <span className={`text-right text-sm ${highlight ? "text-white/80" : "text-stone-500"}`}>
          {renoRate ? (
            <>
              <b className={highlight ? "text-white" : "text-navy"}>{renoRate.pct}%</b> du HT travaux
              {renoRate.min ? (
                <>
                  <br />
                  Minimum {euro(renoRate.min)} HT
                </>
              ) : null}
            </>
          ) : (
            "—"
          )}
        </span>
      </div>
      <div className={`h-px ${highlight ? "bg-white/15" : "bg-stone-200"}`} />
      <div className="flex items-center justify-between gap-4">
        <span className={`flex items-center gap-2 text-sm font-semibold ${highlight ? "text-white" : "text-navy"}`}>
          <Building2 className="h-4 w-4" /> Maison neuve
        </span>
        <span className={`text-right text-sm ${highlight ? "text-white/80" : "text-stone-500"}`}>
          {neuveRate ? (
            <>
              <b className={highlight ? "text-white" : "text-navy"}>{neuveRate.pct}%</b> du HT travaux
              <br />
              {neuveRate.min ? `Minimum ${euro(neuveRate.min)} HT` : "Sans minimum"}
            </>
          ) : (
            "—"
          )}
        </span>
      </div>
    </div>

    <button
      data-testid={`${testid}-cta`}
      onClick={onCta}
      className={`mt-7 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-colors duration-300 ${
        highlight ? "bg-cream text-navy hover:bg-cognac hover:text-white" : "bg-navy text-white hover:bg-cognac"
      }`}
    >
      Parler de mon projet <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);

export default function TarifsPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const goEstimate = () => navigate("/estimer");

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => setPricing({}));
  }, []);

  return (
    <PageShell>
      <PageHero
        testid="page-tarifs-hero"
        label="Nos tarifs"
        title="Un accompagnement adapté à votre projet"
        sub="Choisissez le niveau d'accompagnement dont vous avez besoin, de la conception de votre projet jusqu'à la réception des travaux."
      />

      <section className="bg-ivory pb-16 sm:pb-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="grid gap-6 sm:grid-cols-2" data-testid="pricing-formula-cards">
            <Reveal>
              <FormulaCard
                testid="pricing-card-essentiel"
                title="Essentiel"
                tagline="Pour préparer, concevoir et organiser le projet."
                items={ESSENTIEL_ITEMS}
                renoRate={pricing?.formulas?.RENOVATION?.ESSENTIEL}
                neuveRate={pricing?.formulas?.NEUVE?.ESSENTIEL}
                onCta={goEstimate}
              />
            </Reveal>
            <Reveal delay={0.08}>
              <FormulaCard
                testid="pricing-card-complet"
                title="Pack complet"
                tagline="L'accompagnement jusqu'à la réception du chantier."
                items={COMPLETE_ITEMS}
                renoRate={pricing?.formulas?.RENOVATION?.COMPLETE}
                neuveRate={pricing?.formulas?.NEUVE?.COMPLETE}
                highlight
                onCta={goEstimate}
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-center font-display text-2xl font-medium text-navy sm:text-3xl">Comparez les deux formules</h2>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-10 hidden overflow-hidden rounded-2xl border border-stone-200 sm:block" data-testid="pricing-comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white text-left">
                    <th className="px-5 py-4 font-semibold text-navy">Prestation</th>
                    <th className="px-5 py-4 text-center font-semibold text-navy">Essentiel</th>
                    <th className="px-5 py-4 text-center font-semibold text-navy">Pack complet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white">
                  {COMPARISON_ROWS.map((r) => (
                    <tr key={r.label}>
                      <td className="px-5 py-3.5 text-stone-600">{r.label}</td>
                      <td className="px-5 py-3.5 text-center">
                        {r.essentiel ? <Check className="mx-auto h-4 w-4 text-sage" /> : <Minus className="mx-auto h-4 w-4 text-stone-300" />}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {r.complete ? <Check className="mx-auto h-4 w-4 text-cognac" /> : <Minus className="mx-auto h-4 w-4 text-stone-300" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-10 space-y-2.5 sm:hidden" data-testid="pricing-comparison-list">
              {COMPARISON_ROWS.map((r) => (
                <div key={r.label} className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3">
                  <span className="text-sm text-stone-600">{r.label}</span>
                  <span className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-wide">
                    <span className={`flex items-center gap-1 ${r.essentiel ? "text-sage" : "text-stone-300"}`}>
                      {r.essentiel ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />} Ess.
                    </span>
                    <span className={`flex items-center gap-1 ${r.complete ? "text-cognac" : "text-stone-300"}`}>
                      {r.complete ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />} Pack
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac">À la carte</p>
            <h2 className="mt-3 font-display text-2xl font-medium text-navy sm:text-3xl">Besoin uniquement d'une prestation ?</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
              Certaines démarches peuvent être commandées seules, ou à un tarif préférentiel si vous suivez déjà l'une de nos formules.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="pricing-a-la-carte-grid">
            {(pricing?.a_la_carte || []).map((item, i) => (
              <Reveal key={item.id} delay={Math.min(i * 0.04, 0.2)}>
                <div className="h-full rounded-2xl border border-stone-200 bg-white p-6" data-testid={`pricing-item-${item.id}`}>
                  <p className="font-semibold text-navy">{item.label}</p>
                  {item.quote_only ? (
                    <p className="mt-3 text-sm font-bold text-cognac">Sur devis</p>
                  ) : item.options.length === 1 ? (
                    <p className="mt-3 text-lg font-bold text-navy">{euro(item.options[0].price_ht)} HT</p>
                  ) : (
                    <ul className="mt-3 space-y-1.5">
                      {item.options.map((opt) => (
                        <li key={opt.tier || "none"} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-stone-500">{TIER_LABEL[opt.tier]}</span>
                          <span className={`font-bold ${opt.included ? "text-sage" : "text-navy"}`}>
                            {opt.included ? "Inclus" : `${euro(opt.price_ht)} HT`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-stone-200 bg-white p-7 sm:p-8" data-testid="pricing-division-terrain">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-cognac">
                <MapPinned className="h-4 w-4" /> Division de terrain
              </p>
              <ul className="mt-5 space-y-3">
                {(pricing?.division_terrain?.items || []).map((it) => (
                  <li key={it.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-stone-600">{it.label}</span>
                    <span className="text-right font-bold text-navy">
                      {euro(it.price_ht)} HT
                      {it.note ? <span className="ml-1 font-normal text-stone-400">{it.note}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs leading-relaxed text-stone-400">{pricing?.division_terrain?.cumulative_note}</p>
              <div className="mt-4 space-y-1 border-t border-stone-100 pt-4 text-xs text-stone-500">
                <p>{pricing?.division_terrain?.geometre_note}</p>
                <p>{pricing?.division_terrain?.permis_note}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.06}>
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-2xl border border-stone-200 bg-white p-7 sm:p-8" data-testid="pricing-vues-3d">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-cognac">
                  <Boxes className="h-4 w-4" /> Vues 3D
                </p>
                <ul className="mt-5 space-y-3">
                  {(pricing?.vues_3d?.items || []).map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-stone-600">{it.label}</span>
                      <span className="font-bold text-navy">{it.quote_only ? "Sur devis" : `${euro(it.price_ht)} HT`}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs leading-relaxed text-stone-400">{pricing?.vues_3d?.note}</p>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-white p-7 sm:p-8" data-testid="pricing-reception-ponctuelle">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-cognac">
                  <Camera className="h-4 w-4" /> Réception ponctuelle
                </p>
                <p className="mt-4 text-sm font-semibold text-navy">{pricing?.reception_ponctuelle?.title}</p>
                <ul className="mt-3 space-y-3">
                  {(pricing?.reception_ponctuelle?.items || []).map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-stone-600">{it.label}</span>
                      <span className="text-right font-bold text-navy">
                        {it.included ? "Incluse" : `${euro(it.price_ht)} HT`}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs leading-relaxed text-stone-400">{pricing?.reception_ponctuelle?.note}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
